<template>
  <Card>
    <template #title>
      <div class="p-d-flex p-justify-between">
        <div>แบบประเมิน</div>
        <div>
          <Button
            label="สร้างใหม่"
            icon="pi pi-plus"
            @click="onNewQuestion"
          ></Button>

          <back-button class="p-ml-2" />
        </div>
      </div>
    </template>
    <template #content>
      <div class="p-grid">
        <div class="p-col-12">
          <DataTable
            :value="questions"
            dataKey="id"
            class="p-datatable-sm"
            paginator
            :rows="10"
          >
            <template #empty="">ไม่พบข้อมูล</template>
            <Column
              field="id"
              header="ID"
              headerStyle="width:5rem"
              bodyStyle="width:5rem"
            ></Column>
            <Column field="name" header="ชื่อ"></Column>
            <Column field="createDate" header="สร้างเมื่อ">
              <template #body="props">
                {{ formatAppDate(props.data.createDate) }}
              </template>
            </Column>
            <Column field="createBy" header="สร้างโดย">
              <template #body="props">
                {{ props.data.createByEmployee?.id }} -
                {{ props.data.createByEmployee?.prename }}
                {{ props.data.createByEmployee?.name }}
                {{ props.data.createByEmployee?.surname }}
              </template>
            </Column>
            <Column field="updateDate" header="แก้ไขล่าสุดเมื่อ">
              <template #body="props">
                {{ formatAppDate(props.data.updateDate) }}
              </template>
            </Column>
            <Column field="updateBy" header="แก้ไขล่าสุดโดย">
              <template #body="props">
                {{ props.data.updateByEmployee?.id }} -
                {{ props.data.updateByEmployee?.prename }}
                {{ props.data.updateByEmployee?.name }}
                {{ props.data.updateByEmployee?.surname }}
              </template>
            </Column>
            <Column headerStyle="width:10rem" bodyStyle="width:10rem">
              <template #body="props">
                <Button
                  class="p-button-rounded"
                  icon="pi pi-pencil"
                  @click="onEdit(props.data.id)"
                ></Button>
                <Button
                  class="p-button-rounded p-ml-2 p-button-warning"
                  icon="pi pi-copy"
                  @click="onCopy(props.data.id)"
                  v-tooltip="`ทำซ้ำ`"
                ></Button>
                <Button
                  class="p-button-rounded p-ml-2 p-button-danger"
                  icon="pi pi-trash"
                  @click="onDelete(props.data.id)"
                  v-tooltip="`ลบ`"
                ></Button>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </template>
  </Card>

  <Dialog v-model:visible="showPopupCopy" modal header="ทำซ้ำ">
    <div class="p-grid">
      <div class="p-col-12 p-fluid">
        <span class="p-float-label">
          <InputText id="copyName" v-model="copyName"></InputText>
          <label for="copyName">ระบุชื่อแบบประเมินใหม่ที่ต้องการ</label>
        </span>
      </div>
      <div class="p-col-12">
        <Button
          label="ยืนยัน"
          icon="pi pi-copy"
          @click="onConfirmCopy"
        ></Button>
        <Button
          label="ยกเลิก"
          icon="pi pi-times"
          class="p-button-warning p-ml-2"
          @click="() => (showPopupCopy = false)"
        ></Button>
      </div>
    </div>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import { usePlugin } from "@/plugins";
import {
  Question,
  GetQuestionRequest,
  GetQuestionResponse,
  CopyQuestionRequest,
  CopyQuestionResponse,
  DeleteQuestionRequest,
} from "@/types";
import BackButton from "@/components/BackButton.vue";

export default defineComponent({
  name: "QuestionList",
  components: { BackButton },
  setup() {
    const {
      router,
      setLoading,
      error,
      success,
      axios,
      formatAppDate,
      confirmation,
    } = usePlugin();
    const onNewQuestion = () => {
      router.push({ name: "newQuestion" });
    };

    const questions = ref<Question[]>([]);
    const loadDocuments = async () => {
      setLoading(true);
      try {
        var result = await axios.post<GetQuestionResponse>(
          "api/v1/services/question/get",
          {} as GetQuestionRequest
        );
        questions.value = result.data.questions ?? [];
        success("ดึงข้อมูลสำเร็จแล้ว");
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    const onEdit = (id: number) => {
      router.push({ name: "editQuestion", params: { id: id } });
    };

    const showPopupCopy = ref(false);
    const copyName = ref("");
    const copyId = ref(0);
    const onCopy = (id: number) => {
      showPopupCopy.value = true;
      copyId.value = id;
    };

    const onConfirmCopy = async () => {
      setLoading(true);
      try {
        var result = await axios.post<CopyQuestionResponse>(
          "api/v1/services/question/copy",
          {
            questionId: copyId.value,
            description: copyName.value,
          } as CopyQuestionRequest
        );
        success("Copy ข้อมูลเรียบร้อยแล้ว");
        router.push({
          name: "editQuestion",
          params: { id: result.data.questionId },
        });
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    const onDelete = async (id: number) => {
      confirmation.require({
        message: "กรุณายืนยันเพื่อทำการลบแบบประเมินนี้",
        header: `ลบแบบประเมิน ${id}`,
        acceptLabel: "ยืนยันลบ",
        accept: async () => {
          setLoading(false);
          try {
            await axios.post("api/v1/services/question/delete", {
              questionId: id,
            } as DeleteQuestionRequest);

            success("ลบข้อมูลเรียบร้อยแล้ว");
            await loadDocuments();
          } catch (e) {
            error(e);
          } finally {
            setLoading(false);
          }
        },
        rejectLabel: "ยกเลิก",
      });
    };

    onMounted(async () => {
      await loadDocuments();
    });

    return {
      onNewQuestion,
      questions,
      onEdit,
      formatAppDate,
      showPopupCopy,
      onCopy,
      copyName,
      onConfirmCopy,
      onDelete,
    };
  },
});
</script>

<style></style>
